import React, { useState, useEffect } from 'react';
import Input from '../../../../../../components/form-control/Input';
import { Formik } from 'formik';
import { getActiveRiskTypes, searchVehicleMake, searchVehicleModelsByMake } from '../../../../../../api/requests';
import { IMultiSelectItem, MultiSelect } from '../../../../../../components/form-control/MultiSelect';
import InputSelectMenu from '../../../../../../components/form-control/InputSelectMenu';
import { bodyTypeMappingFromNumberToString, getSelectableManufacturingYears, getVehicleBodyTypeNumberFromKey, phoneValidationSchema, removeUnderscoresAndCapitalize, showExcessBoughtField, showToast } from '../../../../../../utils/miscFunctions';
import Button from '../../../../../../components/form-control/Button';
import { FleetVehicle } from '../index';
import { useSelector } from 'react-redux';
import { getEnumsFromState } from '../../../../../../states/slices/resourceSlice';
import { InputRadio } from '../../../../../../components/form/InputRadio';
import { useQuery } from '@tanstack/react-query';
import SelectDropdown from '../../../../../../components/form-control/SelectDropdown';
import { ISelectMenuItemValue } from '../../../../../../components/form-control/SelectMenu';
import usePartner from '../../../../../../hooks/partnerHook';
import { IEnum, IRequestResponse } from '../../../../../../resources/interfaces';
import * as Yup from 'yup';

// 1. Update the interface
interface PolicyFleetVehicleModalProps {
  onClose: () => void;
  vehicle?: FleetVehicle | null;
  onProceed: (vehicle: FleetVehicle) => void;
  vehicles?: FleetVehicle[];
  mode?: 'add' | 'edit';
}

const CreatePolicyFleetVehicleModal: React.FC<PolicyFleetVehicleModalProps> = ({ onClose, vehicle, onProceed, vehicles }) => {
  const { fetchAllPortfolios } = usePartner()
  const [validManufacturingYears, setValidManufacturingYears] = useState(getSelectableManufacturingYears());
  const [vehiclesList, setVehiclesList] = useState<FleetVehicle[]>(vehicles || []);
  const [vehicleMakes, setVehicleMakes] = useState<any>([]);
  const [selectedVehicleMake, setSelectedVehicleMake] = useState<string>('');
  const [vehicleMakePrefix, setVehicleMakePrefix] = useState<string>('');
  const [vehicleModels, setVehicleModels] = useState<any>([]);
  const [loadingMakes, setLoadingMakes] = useState<boolean>(false);
  const enums = useSelector(getEnumsFromState);
  const [riskTypes, setRiskTypes] = useState<any>();


  const seachForVehicleModel = async () => {
    try {
      if (selectedVehicleMake) {
        let models = await searchVehicleModelsByMake(selectedVehicleMake?.toUpperCase()?.trim());
        if (models?.data) {
          setVehicleModels(models?.data);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAllActiveRiskTypes = () => {
    getActiveRiskTypes()
      .then((response: any) => {
        setRiskTypes(response);
      })
      .catch((error: any) => {
        console.error('Error fetching risk types:', error);
      });
  };

  const seachForVehicleMake = async () => {
    try {
      let makes = await searchVehicleMake(vehicleMakePrefix?.toUpperCase());
      if (makes) {
        setVehicleMakes(makes?.data?.splice(0, 100));
        setTimeout(() => {
          setLoadingMakes(false);
        }, 3000);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const saveVehicle = async (values: FormValues) => {
    if (!values) return false;

    try {
      const updatedVehicle: FleetVehicle = {
        maker: values.make || '',
        model: values.model || '',
        yearIssued: values.manufacturingyear ? parseInt(values.manufacturingyear) : 0,
        color: values.color || '',
        chassisNumber: values.chassisnumber || '',
        registrationNumber: values.registrationnumber || '',
        issuedValue: parseFloat(values.issuedvalue) || 0,
        vehicleType: 'fleet',
        vehicleUsage: values.risktypeid,
        vehicleBodyType: getVehicleBodyTypeNumberFromKey(values.vehiclebodytype || ''),
        registrationYear: values.registrationyear ? parseInt(values.registrationyear) : 0,
        cc: parseInt(values.cc) || 0,
        maxSeat: parseInt(values.maxseat) || 0,
        coverType: values.portfolioid,
        region: values.region || '',
        insurerPortfolioId: parseInt(values.portfolioid) || 0,
        riskTypeId: parseInt(values.risktypeid) || 0,
        excessBought: values.excessbought || '',
        extraTppdlLimit: parseFloat(values.extratppdllimit) || 0,
        driver: values.maindriver || '',
        driverPhone: values.driverphonenumber || '',
        driverHasComplications: values.driverhascomplications === 'Yes' ? 'Yes' : 'No',
        isInGoodRepairState: values.vehicleingoodstateofrepair === 'Yes' ? 'Yes' : 'No',
        previousInsuranceDeclined: values.previousinsurancedeclined === 'Yes' ? 'Yes' : 'No',
        vehicleInGoodRepairState: values.vehicleingoodstateofrepair === 'Yes' ? 'Yes' : 'No',
        notes: vehicle?.notes || '',
        driverComplications: values.driverhascomplications === 'Yes' ? 'Yes' : 'No',
        driverEmail: vehicle?.driverEmail || '',
        driverLicense: vehicle?.driverLicense || '',
        hasInstalledAccessories: vehicle?.hasInstalledAccessories === 'Yes' ? 'Yes' : 'No',
        hasInterestedParties: vehicle?.hasInterestedParties === 'Yes' ? 'Yes' : 'No',
        images: vehicle?.images || '',
        installedAccessoriesDetails: vehicle?.installedAccessoriesDetails || '',
        installedAccessoriesValue: vehicle?.installedAccessoriesValue || 0,
        state: vehicle?.state || '',
        // Per-vehicle cover window; strings map to undefined when blank
        // so the BE falls back to the fleet-level dates rather than
        // treating "" as a real value.
        startDate: values.startdate ? String(values.startdate) : undefined,
        endDate: values.enddate ? String(values.enddate) : undefined,
      };

      onProceed(updatedVehicle);
      return true;
    } catch (error: any) {
      console.error('Error saving vehicle:', error);
      showToast.error(error?.description || 'Failed to save vehicle. Please try again.');
      return false;
    }
  };

  const { data: insurerPortfolio = [] } = useQuery({
    queryKey: ['insurerPortfolio'],
    queryFn: () => fetchAllPortfolios(),
  })

  useQuery({
    queryKey: ['getAllActiveRiskTypes'],
    queryFn: () => fetchAllActiveRiskTypes(),
  })

  useEffect(() => {
    if (selectedVehicleMake) {
      setVehicleModels([]);
      seachForVehicleModel();
    }
  }, [selectedVehicleMake]);

  useEffect(() => {
    setLoadingMakes(true);
    seachForVehicleMake();
  }, [vehicleMakePrefix]);

  return (
    <div className="p-6">
      <Formik
        initialValues={{
          make: vehicle?.maker || '',
          model: vehicle?.model || '',
          vehiclebodytype: bodyTypeMappingFromNumberToString[vehicle?.vehicleBodyType || ''] || '',
          registrationyear: vehicle?.registrationYear || '',
          manufacturingyear: vehicle?.yearIssued || '',
          registrationnumber: vehicle?.registrationNumber || '',
          issuedvalue: vehicle?.issuedValue || '',
          maxseat: vehicle?.maxSeat || '',
          cc: vehicle?.cc || '',
          chassisnumber: vehicle?.chassisNumber || '',
          color: vehicle?.color || '',
          risktypeid: vehicle?.riskTypeId || '',
          portfolioid: vehicle?.insurerPortfolioId || '',
          excessbought: vehicle?.excessBought || null,
          region: vehicle?.region || '',
          maindriver: vehicle?.driver || '',
          driverphonenumber: vehicle?.driverPhone || '',
          driverhascomplications: vehicle?.driverHasComplications || undefined,
          vehicleingoodstateofrepair: vehicle?.isInGoodRepairState || undefined,
          previousinsurancedeclined: vehicle?.previousInsuranceDeclined || undefined,
          extratppdllimit: vehicle?.extraTppdlLimit || undefined,
          startdate: vehicle?.startDate || '',
          enddate: vehicle?.endDate || '',
        }}
        validateOnChange={true}
        validationSchema={Yup.object({
          make: Yup.string().required('Make is required'),
          model: Yup.string().required('Model is required'),
          vehiclebodytype: Yup.string().required('Vehicle body type is required'),
          registrationyear: Yup.number()
            .required('Registration year is required')
            .min(1900, 'Invalid year')
            .max(new Date().getFullYear() + 1, 'Year cannot be in the future'),

          manufacturingyear: Yup.number()
            .required('Manufacturing year is required')
            .min(1900, 'Invalid year')
            .max(new Date().getFullYear() + 1, 'Year cannot be in the future')
            .when('registrationyear', (registrationyear, schema) => {
              if (registrationyear) {
                return schema.max(
                  registrationyear,
                  'Manufacturing year cannot be after registration year'
                );
              }
              return schema;
            }),
          registrationnumber: Yup.string().required('Registration number is required'),
          issuedvalue: Yup.number()
            .required('Issued value is required')
            .min(1, 'Value must be greater than 0'),
          maxseat: Yup.number()
            .required('Maximum seats is required')
            .min(1, 'Must have at least 1 seat'),
          cc: Yup.number()
            .required('Engine capacity (CC) is required')
            .min(1, 'Engine capacity must be greater than 0'),
          chassisnumber: Yup.string().required('Chassis number is required'),
          color: Yup.string().required('Color is required'),
          risktypeid: Yup.string().required('Risk type is required'),
          portfolioid: Yup.string().required('Portfolio ID is required'),
          excessbought: Yup.mixed()
            .when('portfolioid', {
              is: (portfolioid: number | string) => showExcessBoughtField(Number(portfolioid)),
              then: (schema) => schema.required('Excess bought is required').oneOf(['Yes', 'No']),
              otherwise: (schema) => schema.nullable().notRequired()
            }),
          maindriver: Yup.string().required('Main driver is required'),
          driverphonenumber: phoneValidationSchema(),
          driverhascomplications: Yup.mixed().oneOf(['Yes', 'No', true, false], 'Please select an option').required('This field is required'),
          vehicleingoodstateofrepair: Yup.mixed().oneOf(['Yes', 'No', true, false], 'Please select an option').required('This field is required'),
          previousinsurancedeclined: Yup.mixed().oneOf(['Yes', 'No', true, false], 'Please select an option').required('This field is required'),
          extratppdllimit: Yup.number().min(0, 'Must be 0 or greater').nullable()


        })}
        enableReinitialize={true}
        onSubmit={(values: any) => {
          saveVehicle(values);
        }}>
        {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => {
          return (
            <form className="space-y-4" onSubmit={handleSubmit}>

              {/* Driver & Coverage Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mt-4">
                  Driver & Coverage Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Main Driver"
                    name="maindriver"
                    placeholder="Enter main driver's name"
                    value={values.maindriver}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maindriver ? errors.maindriver : undefined}
                    helperText={touched.maindriver ? errors.maindriver : undefined}
                    required
                  />

                  <Input
                    label="Driver Phone"
                    name="driverphonenumber"
                    placeholder="Enter driver's phone number"
                    value={values.driverphonenumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.driverphonenumber ? errors.driverphonenumber : undefined}
                    helperText={touched.driverphonenumber ? errors.driverphonenumber : undefined}
                    required
                  />
                </div>
              </div>

              {/* Vehicle Information */}
              <div className="space-y-4 mb-4">
                <h3 className="text-lg font-medium text-gray-900">Vehicle Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MultiSelect
                    label="Vehicle Make"
                    selected={[values?.make || '']}
                    onChange={(val: string[]) => {
                      setFieldValue('make', val?.[0]);
                      if (val && val.length > 0) {
                        setSelectedVehicleMake(val[0]);
                      }
                    }}
                    list={vehicleMakes?.map(
                      (make: any) =>
                        ({
                          name: make,
                          value: make
                        }) as IMultiSelectItem
                    )}
                    placeholder="Search and select your vehicle make"
                    selectInputClassName="!shadow-none"
                    error={touched.make ? String(errors.make || '') : undefined}
                    helperText={touched.make ? String(errors.make || '') : undefined}
                    required
                    description="vehicle make - manufacturer"
                    onQueryChange={(query: string) => {
                      setVehicleMakePrefix(query);
                    }}
                    multiSelect={false}
                    loading={loadingMakes}
                  />

                  <InputSelectMenu
                    name="model"
                    label="Vehicle Model"
                    placeholder="Select your vehicle model"
                    description="vehicle model - type"
                    list={vehicleModels}
                    listSelectedValue={values.model}
                    onChange={(val: string) => {
                      setFieldValue('model', val);
                    }}
                    required
                    search
                    searchPlaceHolder="Enter your vehicle model. Eg. Yaris"
                    sort
                    error={touched.model ? errors.model : undefined}
                    helperText={touched.model ? errors.model : undefined}
                  />

                  <SelectDropdown
                    name="vehiclebodytype"
                    label="Body Type"
                    placeholder="Select body type"
                    list={enums?.vehicleBodyTypes?.map((type: IEnum) => ({
                      name: removeUnderscoresAndCapitalize(type.title),
                      value: type.key
                    }))}
                    listSelectedValue={values.vehiclebodytype}
                    onChange={(val: string) => {
                      setFieldValue('vehiclebodytype', val);
                    }}
                    required
                    error={touched.vehiclebodytype ? errors.vehiclebodytype : undefined}
                    helperText={touched.vehiclebodytype ? errors.vehiclebodytype : undefined}
                  />

                  <Input
                    label="Registration Number"
                    name="registrationnumber"
                    placeholder="Enter registration number"
                    value={values.registrationnumber?.toUpperCase()}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.registrationnumber ? errors.registrationnumber : undefined}
                    helperText={touched.registrationnumber ? errors.registrationnumber : undefined}
                    required
                  />

                  <Input
                    label="Registration Year"
                    name="registrationyear"
                    type="number"
                    placeholder="Enter registration year"
                    value={values.registrationyear}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.registrationyear ? errors.registrationyear : undefined}
                    helperText={touched.registrationyear ? errors.registrationyear : undefined}
                  />

                  <InputSelectMenu
                    label="Manufactured Year"
                    name="manufacturingyear"
                    listSelectedValue={values.manufacturingyear}
                    onChange={(val: string | null) => {
                      setFieldValue('manufacturingyear', val);
                    }}
                    onBlur={handleBlur}
                    list={validManufacturingYears}
                    // description="The year the vehicle was manufactured"
                    placeholder="Select manufactured year"
                    required
                    sort
                    emptyListMessage="Select manufactured year to proceed"
                    error={touched.manufacturingyear ? errors.manufacturingyear : undefined}
                    helperText={touched.manufacturingyear ? errors.manufacturingyear : undefined}
                  />


                  <SelectDropdown
                    label="Cover Type"
                    name="portfolioid"
                    listSelectedValue={values.portfolioid}
                    onChange={(val: number) => {
                      setFieldValue('portfolioid', val);
                    }}
                    onBlur={handleBlur}
                    search
                    searchPlaceHolder="Search by cover type ..."
                    list={Array.isArray(insurerPortfolio)
                      ? insurerPortfolio.map((item: any) => ({
                        name: item?.name || '',
                        value: item?.id || '',
                      }))
                      : []}
                    placeholder="Select cover type"
                    error={touched.portfolioid ? errors.portfolioid : undefined}
                    helperText={touched?.portfolioid ? errors.portfolioid : undefined}
                    required
                  />


                  {showExcessBoughtField(Number(values.portfolioid)) && (
                    <SelectDropdown
                      name="excessbought"
                      label="Excess Bought"
                      listSelectedValue={values.excessbought}
                      onChange={(val: string | null) => {
                        setFieldValue('excessbought', val);
                      }}
                      onBlur={handleBlur}
                      list={[
                        { name: 'Yes', value: 'Yes' },
                        { name: 'No', value: 'No' }
                      ]}
                      placeholder="Select excess bought"
                      error={touched.excessbought && errors.excessbought ? String(errors.excessbought) : undefined}
                      helperText={touched.excessbought && errors.excessbought ? String(errors.excessbought) : undefined}
                      required={showExcessBoughtField(Number(values.portfolioid))}
                    />
                  )}

                  <SelectDropdown
                    label="Usage"
                    name="risktypeid"
                    listSelectedValue={values.risktypeid}
                    onChange={(val: ISelectMenuItemValue | null) => {
                      setFieldValue('risktypeid', val);
                    }}
                    onBlur={handleBlur}
                    search
                    searchPlaceHolder="Search by usage ..."
                    list={riskTypes?.map((riskType: any) => ({
                      name: riskType?.name,
                      value: riskType?.id,
                    }))}
                    placeholder="Select usage"
                    error={touched.risktypeid ? errors.risktypeid : undefined}
                    helperText={touched?.risktypeid ? errors.risktypeid : undefined}
                    required
                  />

                  <Input
                    label="Engine CC"
                    name="cc"
                    type="number"
                    placeholder="Enter engine capacity"
                    value={values.cc}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.cc ? errors.cc : undefined}
                    helperText={touched.cc ? errors.cc : undefined}
                  />

                  <Input
                    label="Number of Passengers"
                    name="maxseat"
                    type="number"
                    placeholder="Enter number of passengers"
                    value={values.maxseat}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.maxseat ? errors.maxseat : undefined}
                    helperText={touched.maxseat ? errors.maxseat : undefined}
                  />

                  <Input
                    label="Chassis Number"
                    name="chassisnumber"
                    placeholder="Enter chassis number"
                    value={values.chassisnumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.chassisnumber ? errors.chassisnumber : undefined}
                    helperText={touched.chassisnumber ? errors.chassisnumber : undefined}
                  />

                  <Input
                    label="Color"
                    name="color"
                    placeholder="Enter vehicle color"
                    value={values.color}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.color ? errors.color : undefined}
                    helperText={touched.color ? errors.color : undefined}
                  />

                  <Input
                    type="number"
                    label="Insured Value"
                    name="issuedvalue"
                    placeholder="Enter insured value"
                    value={values.issuedvalue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.issuedvalue ? errors.issuedvalue : undefined}
                    helperText={touched.issuedvalue ? errors.issuedvalue : undefined}
                    required
                  />

                  {/* Per-vehicle cover window. Optional here — when left
                      blank the vehicle inherits the fleet-level dates on
                      the BE. Populate to give this specific car its own
                      start / end (mid-cycle additions, hire-purchase
                      windows, replacement cars on later dates). */}
                  <Input
                    type="date"
                    label="Start Date"
                    name="startdate"
                    placeholder="yyyy-mm-dd"
                    value={values.startdate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.startdate ? String(errors.startdate || '') : undefined}
                    helperText={touched.startdate ? String(errors.startdate || '') : undefined}
                  />

                  <Input
                    type="date"
                    label="End Date"
                    name="enddate"
                    placeholder="yyyy-mm-dd"
                    value={values.enddate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.enddate ? String(errors.enddate || '') : undefined}
                    helperText={touched.enddate ? String(errors.enddate || '') : undefined}
                  />

                </div>
              </div>

              {/* Risk Assessment */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Risk Assessment</h3>

                <div className="space-y-4">
                  <InputRadio
                    name="driverhascomplications"
                    label={'Driver has complications?'}
                    options={[
                      { name: 'Yes', value: 'Yes' },
                      { name: 'No', value: 'No' }
                    ]}
                    selected={
                      values.driverhascomplications !== undefined
                        ? values.driverhascomplications === 'Yes'
                          ? 'Yes'
                          : 'No'
                        : undefined
                    }
                    isFlex
                    onSelect={handleChange}
                    error={touched?.driverhascomplications ? String(errors.driverhascomplications || '') : undefined}
                    helperText={touched?.driverhascomplications ? String(errors.driverhascomplications || '') : undefined}
                  />

                  <InputRadio
                    name="vehicleingoodstateofrepair"
                    label={'Is your vehicle in a good state of repair?'}
                    options={[
                      { name: 'Yes', value: 'Yes' },
                      { name: 'No', value: 'No' }
                    ]}
                    selected={
                      values.vehicleingoodstateofrepair !== undefined
                        ? values.vehicleingoodstateofrepair === 'Yes'
                          ? 'Yes'
                          : 'No'
                        : undefined
                    }
                    isFlex
                    onSelect={handleChange}
                    error={touched?.vehicleingoodstateofrepair ? String(errors.vehicleingoodstateofrepair || '') : undefined}
                    helperText={touched?.vehicleingoodstateofrepair ? String(errors.vehicleingoodstateofrepair || '') : undefined}
                  />

                  <InputRadio
                    name="previousinsurancedeclined"
                    label={'Previous insurance declined?'}
                    options={[
                      { name: 'Yes', value: 'Yes' },
                      { name: 'No', value: 'No' }
                    ]}
                    selected={
                      values.previousinsurancedeclined !== undefined
                        ? values.previousinsurancedeclined === 'Yes'
                          ? 'Yes'
                          : 'No'
                        : undefined
                    }
                    isFlex
                    onSelect={handleChange}
                    error={touched?.previousinsurancedeclined ? String(errors.previousinsurancedeclined || '') : undefined}
                    helperText={touched?.previousinsurancedeclined ? String(errors.previousinsurancedeclined || '') : undefined}
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t">
                <Button label="Cancel" type="button" onClick={onClose} />
                <Button
                  type="submit"
                  label={vehicle ? 'Update Vehicle' : 'Add Vehicle'}
                  bgColor="primary"
                />
              </div>
            </form>
          )
        }}
      </Formik>
    </div>
  );
};

export default CreatePolicyFleetVehicleModal;


// Save vehicle (add or update)
interface FormValues {
  [key: string]: any;
  make: string;
  model: string;
  vehiclebodytype: string;
  registrationyear: string;
  manufacturingyear: string;
  registrationnumber: string;
  issuedvalue: string;
  maxseat: string;
  cc: string;
  chassisnumber: string;
  color: string;
  risktypeid: string;
  portfolioid: string;
  excessbought: string;
  region: string;
  maindriver: string;
  driverphonenumber: string;
  driverhascomplications: string;
  vehicleingoodstateofrepair: string;
  previousinsurancedelcined: string;
  extratppdllimit: string;
}
